import React, { useState, useMemo } from "react";
import { AMMO_TYPES } from "../../../../constants/ship";
import { AmmunitionStorage } from "../../../../types/ship";
import { formatCurrency } from "../../../../utils/formatting";

interface BreakawayAmmunitionManagerProps {
  sectionId: string;
  sectionName: string;
  ammunition: AmmunitionStorage[];
  onAddAmmunition: (sectionId: string, ammoType: string, quantity: number) => void;
  onRemoveAmmunition: (sectionId: string, ammoId: string) => void;
  onUpdateAmmunitionQuantity: (sectionId: string, ammoId: string, quantity: number) => void;
}

const BreakawayAmmunitionManager: React.FC<BreakawayAmmunitionManagerProps> = ({
  sectionId,
  sectionName,
  ammunition,
  onAddAmmunition,
  onRemoveAmmunition,
  onUpdateAmmunitionQuantity,
}) => {
  // Category and mount type mapping for ammo types
  const ammoCategoryMap: Record<string, { category: string; mount: string }> = useMemo(() => ({
    // Missiles
    basicMissile: { category: "Missile", mount: "Fixed/Turret" },
    advancedMissile: { category: "Missile", mount: "Fixed/Turret" },
    nuclearMissile: { category: "Missile", mount: "Fixed/Turret" },
    fragmentationMissile: { category: "Missile", mount: "Fixed/Turret" },
    decoyMissile: { category: "Missile", mount: "Fixed/Turret" },
    antiTorpedoMissile: { category: "Missile", mount: "Fixed/Turret" },
    ionMissile: { category: "Missile", mount: "Fixed/Turret" },
    jumpbreakerMissile: { category: "Missile", mount: "Fixed/Turret" },
    longRangeMissile: { category: "Missile", mount: "Fixed/Turret" },
    multiWarheadMissile: { category: "Missile", mount: "Fixed/Turret" },
    antimatterMissile: { category: "Missile", mount: "Fixed/Turret" },
    ortilleryMissile: { category: "Missile", mount: "Fixed/Turret" },
    shockwaveMissile: { category: "Missile", mount: "Fixed/Turret" },
    orbitalStrikeMissile: { category: "Orbital Strike", mount: "Fixed/Turret" },
    // Torpedoes
    basicTorpedo: { category: "Torpedo", mount: "Bay" },
    advancedTorpedoBarbette: { category: "Torpedo", mount: "Barbette" },
    advancedTorpedo: { category: "Torpedo", mount: "Bay" },
    standardTorpedo: { category: "Torpedo", mount: "Bay" },
    antimatterTorpedo: { category: "Torpedo", mount: "Bay" },
    antimatterBombPumpedTorpedo: { category: "Torpedo", mount: "Bay" },
    antiradiationTorpedo: { category: "Torpedo", mount: "Bay" },
    bombPumpedTorpedo: { category: "Torpedo", mount: "Bay" },
    ionTorpedo: { category: "Torpedo", mount: "Bay" },
    multiWarheadAntimatterTorpedo: { category: "Torpedo", mount: "Bay" },
    multiWarheadStandardTorpedo: { category: "Torpedo", mount: "Bay" },
    multiWarheadNuclearTorpedo: { category: "Torpedo", mount: "Bay" },
    nuclearTorpedo: { category: "Torpedo", mount: "Bay" },
    ortilleryTorpedo: { category: "Torpedo", mount: "Bay" },
    plasmaTorpedo: { category: "Torpedo", mount: "Bay" },
    // Sand Canisters
    basicSand: { category: "Sand Canister", mount: "Fixed/Turret" },
    advancedSand: { category: "Sand Canister", mount: "Fixed/Turret" },
    sandCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    antiPersonnelCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    chaffCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    pebbleCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    sandcutterCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    // Railgun
    railgunBarbetteAmmo: { category: "Railgun", mount: "Barbette" },
    railgunAmmo: { category: "Railgun", mount: "Bay" },
    railgunSpinalAmmo: { category: "Railgun", mount: "Spinal Mount" },
    railgunBaySmallAmmo: { category: "Railgun", mount: "Bay" },
    railgunBayMediumAmmo: { category: "Railgun", mount: "Bay" },
    railgunBayLargeAmmo: { category: "Railgun", mount: "Bay" },
    // Mass Driver
    massDriverSpinalAmmo: { category: "Mass Driver", mount: "Spinal Mount" },
    massDriverBaySmallAmmo: { category: "Mass Driver", mount: "Bay" },
    massDriverBayMediumAmmo: { category: "Mass Driver", mount: "Bay" },
    massDriverBayLargeAmmo: { category: "Mass Driver", mount: "Bay" },
  }), []);

  // List of categories for dropdown 1
  const weaponCategories = [
    "Missile",
    "Torpedo",
    "Sand Canister",
    "Railgun",
    "Mass Driver",
    "Orbital Strike",
  ];

  // List of mount types for dropdown 2
  const mountTypeLabels: Record<string, string> = {
    "Fixed/Turret": "Fixed/Turret",
    Barbette: "Barbette",
    Bay: "Bay",
    "Spinal Mount": "Spinal Mount",
  };

  // State for dropdowns
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedMount, setSelectedMount] = useState<string>("");
  const [selectedAmmoType, setSelectedAmmoType] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);

  // Filter ammo types by selected category
  const ammoTypesByCategory = useMemo(() => {
    return Object.entries(AMMO_TYPES).filter(([id, ammo]) => {
      const map = ammoCategoryMap[id];
      return map && map.category === selectedCategory;
    });
  }, [selectedCategory, ammoCategoryMap]);

  // Get unique mount types for selected category
  const mountTypesForCategory = useMemo(() => {
    const mounts = new Set<string>();
    ammoTypesByCategory.forEach(([id]) => {
      const map = ammoCategoryMap[id];
      if (map && map.mount) mounts.add(map.mount);
    });
    return Array.from(mounts);
  }, [ammoTypesByCategory, ammoCategoryMap]);

  // Filter ammo types by selected mount
  const filteredAmmoTypes = useMemo(() => {
    return ammoTypesByCategory.filter(([id]) => {
      const map = ammoCategoryMap[id];
      if (!selectedMount) return true;
      return map && map.mount === selectedMount;
    });
  }, [ammoTypesByCategory, selectedMount, ammoCategoryMap]);

  // When category changes, reset mount and ammo type
  React.useEffect(() => {
    setSelectedMount("");
    setSelectedAmmoType("");
  }, [selectedCategory]);

  // When mount changes, reset ammo type
  React.useEffect(() => {
    setSelectedAmmoType("");
  }, [selectedMount]);

  // When filtered ammo types change, auto-select the first available
  React.useEffect(() => {
    if (filteredAmmoTypes.length > 0) {
      setSelectedAmmoType(filteredAmmoTypes[0][0]);
    } else {
      setSelectedAmmoType("");
    }
  }, [filteredAmmoTypes]);

  const handleAddAmmunition = () => {
    if (!selectedAmmoType || quantity <= 0) return;
    onAddAmmunition(sectionId, selectedAmmoType, quantity);
    setQuantity(1);
  };

  const handleRemoveAmmunition = (id: string) => {
    onRemoveAmmunition(sectionId, id);
  };

  const handleUpdateQuantity = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveAmmunition(id);
      return;
    }
    onUpdateAmmunitionQuantity(sectionId, id, newQuantity);
  };

  const calculateTotalCost = () => {
    return ammunition.reduce((total: number, ammo: any) => {
      return total + ammo.cost * ammo.quantity;
    }, 0);
  };

  const calculateTotalTons = () => {
    return ammunition.reduce((total: number, ammo: any) => {
      return total + ammo.tonsPerReload * ammo.quantity;
    }, 0);
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
        Ammunition Storage
      </h3>
      
      {/* Section Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Section:</span>
            <span className="ml-2 text-sm text-blue-900 dark:text-blue-100 font-semibold">{sectionName}</span>
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-300">
            {ammunition.length} ammunition type{ammunition.length !== 1 ? 's' : ''} stored
          </div>
        </div>
      </div>

      {/* Ammunition Selection Interface */}
      <div className="space-y-4 mb-6">
        {/* Row 1: Category, Mount Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Weapon Category Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Weapon Category
            </label>
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Choose category...</option>
              {weaponCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Mount Type Select (if relevant) */}
          {mountTypesForCategory.length > 1 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mount Type
              </label>
              <select
                value={selectedMount}
                onChange={e => setSelectedMount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Choose mount...</option>
                {mountTypesForCategory.map(type => (
                  <option key={type} value={type}>{mountTypeLabels[type] || type}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mount Type
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-sm">
                Auto-selected
              </div>
            </div>
          )}
        </div>

        {/* Row 2: Ammunition Type, Quantity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Ammunition Type Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ammunition Type
            </label>
            <select
              value={selectedAmmoType}
              onChange={e => setSelectedAmmoType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:text-gray-100"
              disabled={filteredAmmoTypes.length === 0}
            >
              <option value="">Choose ammunition...</option>
              {filteredAmmoTypes.map(([id, ammo]) => (
                <option key={id} value={id}>
                  {ammo.name} (TL{ammo.tl}) - {ammo.damage}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity and Add Button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quantity
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md flex-1 bg-white dark:bg-gray-700">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-full text-center border-0 focus:ring-0 focus:outline-none text-sm bg-transparent text-gray-900 dark:text-gray-100"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleAddAmmunition}
                disabled={!selectedAmmoType}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      {ammunition.length > 0 && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-600 my-6"></div>
          
          {/* Stored Ammunition Table */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Stored Ammunition ({ammunition.length} type{ammunition.length !== 1 ? 's' : ''})
            </h4>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-white dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ammunition
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cost per Unit
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total Cost
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tons per Unit
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total Tons
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {ammunition.map((ammo: any) => (
                    <tr key={ammo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-3 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            {ammo.name}
                            {typeof ammo.perReload === 'number' && ammo.perReload > 1 && (
                              <span className="px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs font-semibold whitespace-nowrap">
                                {ammo.perReload} per unit
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            TL{ammo.tl} • {ammo.damage} • {ammo.traits.join(", ")}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() =>
                              handleUpdateQuantity(ammo.id, ammo.quantity - 1)
                            }
                            className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            -
                          </button>
                          <span className="mx-3 min-w-[30px] text-center text-sm font-medium text-gray-900 dark:text-gray-100">
                            {ammo.quantity}
                          </span>
                          <button
                            onClick={() =>
                              handleUpdateQuantity(ammo.id, ammo.quantity + 1)
                            }
                            className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right text-sm text-gray-900 dark:text-gray-100">
                        {formatCurrency(ammo.cost)}
                      </td>
                      <td className="px-3 py-3 text-right text-sm text-gray-900 dark:text-gray-100 font-medium">
                        {formatCurrency(ammo.cost * ammo.quantity)}
                      </td>
                      <td className="px-3 py-3 text-right text-sm text-gray-900 dark:text-gray-100">
                        {ammo.tonsPerReload.toFixed(2)}
                      </td>
                      <td className="px-3 py-3 text-right text-sm text-gray-900 dark:text-gray-100 font-medium">
                        {(ammo.tonsPerReload * ammo.quantity).toFixed(2)}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          onClick={() => handleRemoveAmmunition(ammo.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section Totals */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Total Cost:</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(calculateTotalCost())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Total Weight:</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{calculateTotalTons().toFixed(2)} tons</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BreakawayAmmunitionManager;
